import axios from "axios";

const loadImageElement = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.src = src;
  });

/**
 * Loads an image for canvas processing without tainting the canvas.
 *
 * Reading pixels off a cross-origin <img> (e.g. a presigned S3 URL) is blocked
 * by the browser unless the image is loaded as a CORS request — and a
 * credentialed CORS image load can't accept a wildcard `Access-Control-Allow-Origin`,
 * which is what these presigned URLs return. So instead we fetch the bytes
 * anonymously (the same way attachment downloads work) and load them via a
 * same-origin `blob:` URL, which is never considered cross-origin.
 *
 * Returns the loaded image plus a cleanup callback that revokes any object URL.
 */
export const createImage = async (
  url: string,
): Promise<{ image: HTMLImageElement; cleanup: () => void }> => {
  // data:/blob: sources are already same-origin — load them directly.
  if (url.startsWith("data:") || url.startsWith("blob:")) {
    const image = await loadImageElement(url);
    return { image, cleanup: () => {} };
  }

  const response = await axios.get<Blob>(url, {
    responseType: "blob",
    withCredentials: true,
    headers: { "Cache-Control": "no-cache", Pragma: "no-cache", Expires: "0" },
  });
  const objectUrl = URL.createObjectURL(response.data);
  try {
    const image = await loadImageElement(objectUrl);
    return { image, cleanup: () => URL.revokeObjectURL(objectUrl) };
  } catch (error) {
    URL.revokeObjectURL(objectUrl);
    throw error;
  }
};

export function getRadianAngle(degreeValue: number): number {
  return (degreeValue * Math.PI) / 180;
}

/**
 * Returns the new bounding area of a rotated rectangle.
 */
export function rotateSize(
  width: number,
  height: number,
  rotation: number,
): { width: number; height: number } {
  const rotRad = getRadianAngle(rotation);

  return {
    width:
      Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height:
      Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

interface Flip {
  horizontal: boolean;
  vertical: boolean;
}

interface PixelCrop {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * This function was adapted from the one in the ReadMe of https://github.com/DominicTobias/react-image-crop
 */
export default async function getCroppedImg(
  imageSrc: string,
  pixelCrop: PixelCrop,
  rotation = 0,
  flip: Flip = { horizontal: false, vertical: false },
): Promise<BlobPart> {
  const { image, cleanup } = await createImage(imageSrc);
  try {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      throw new Error("Unable to get canvas context");
    }
    const rotRad = getRadianAngle(rotation);

    // calculate bounding box of the rotated image
    const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
      image.width,
      image.height,
      rotation,
    );

    // set canvas size to match the bounding box
    canvas.width = bBoxWidth;
    canvas.height = bBoxHeight;

    // translate canvas context to a central location to allow rotating and flipping around the center
    ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
    ctx.rotate(rotRad);
    ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
    ctx.translate(-image.width / 2, -image.height / 2);

    // draw rotated image
    ctx.drawImage(image, 0, 0);

    // croppedAreaPixels values are bounding box relative
    // extract the cropped image using these values
    const data = ctx.getImageData(
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
    );

    // set canvas width to final desired crop size - this will clear existing context
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    // paste generated rotate image at the top left corner
    ctx.putImageData(data, 0, 0);

    // As a blob
    return await new Promise<BlobPart>((resolve, reject) => {
      canvas.toBlob((file) => {
        if (file) {
          resolve(file);
        } else {
          reject(new Error("Failed to generate blob from canvas"));
        }
      }, "image/jpeg");
    });
  } finally {
    cleanup();
  }
}
